import React, { useRef, useEffect, useState } from 'react';
import { IBuildingBlockTestimonialDescription } from '@/types/contentful';
import RichText from '../RichText';
import { BLOCKS } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import cx from 'classnames';
import { Asset } from '../AtomicComponents/Asset';
import { Icon } from '@greenlightme/ui/dist/v2';
import classNames from 'classnames';

export enum TestimonialCardType {
  Avatar = 'Avatar',
  BentoIcon = 'Bento Icon',
  FullWidthImage = 'Full-width Image',
  Star = 'Star'
}

export const StarRatingSVG = ({ className }: any) => {
  return (
    <svg
      width="136"
      height="25"
      viewBox="0 0 136 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_14626_36906)">
        <path
          d="M12 0.374023L15.634 8.38167L24 9.54121L17.88 15.6497L19.4164 24.374L12 20.1417L4.58359 24.374L6.12 15.6497L0 9.54121L8.36596 8.38167L12 0.374023Z"
          fill="currentColor"
        />
      </g>
      <g clipPath="url(#clip1_14626_36906)">
        <path
          d="M40 0.374023L43.634 8.38167L52 9.54121L45.88 15.6497L47.4164 24.374L40 20.1417L32.5836 24.374L34.12 15.6497L28 9.54121L36.366 8.38167L40 0.374023Z"
          fill="currentColor"
        />
      </g>
      <g clipPath="url(#clip2_14626_36906)">
        <path
          d="M68 0.374023L71.634 8.38167L80 9.54121L73.88 15.6497L75.4164 24.374L68 20.1417L60.5836 24.374L62.12 15.6497L56 9.54121L64.366 8.38167L68 0.374023Z"
          fill="currentColor"
        />
      </g>
      <g clipPath="url(#clip3_14626_36906)">
        <path
          d="M96 0.374023L99.634 8.38167L108 9.54121L101.88 15.6497L103.416 24.374L96 20.1417L88.5836 24.374L90.12 15.6497L84 9.54121L92.366 8.38167L96 0.374023Z"
          fill="currentColor"
        />
      </g>
      <g clipPath="url(#clip4_14626_36906)">
        <path
          d="M124 0.374023L127.634 8.38167L136 9.54121L129.88 15.6497L131.416 24.374L124 20.1417L116.584 24.374L118.12 15.6497L112 9.54121L120.366 8.38167L124 0.374023Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
};

const SocialQuoteCardColorTheme = {
  'Brand Strong': {
    background: 'bg-brand-strongest',
    testimonial: 'text-brand-base color-brand-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Brand Subtle': {
    background: 'bg-[#DFF6F0]',
    testimonial: 'text-brand-strongest color-brand-strongest',
    name: 'text-brand-strongest',
    featureReferenced: 'text-brand-strongest'
  },
  'Grape Strong': {
    background: 'bg-accent1-strongest',
    testimonial: 'text-accent1-base color-accent1-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Grape Subtle': {
    background: 'bg-[#EBECFF]',
    testimonial: 'text-accent1-strongest color-accent1-strongest',
    name: 'text-accent1-strongest',
    featureReferenced: 'text-accent1-strongest'
  },
  'Lagoon Strong': {
    background: 'bg-[#00364D]',
    testimonial: 'text-accent2-base color-accent2-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Lagoon Subtle': {
    background: 'bg-[#DFF5FF]',
    testimonial: 'text-accent2-strongest color-accent2-strongest',
    name: 'text-accent2-strongest',
    featureReferenced: 'text-accent2-strongest'
  },
  'Lime Strong': {
    background: 'bg-accent3-strongest',
    testimonial: 'text-accent3-base color-accent3-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Lime Subtle': {
    background: 'bg-[#F0F5C1]',
    testimonial: 'text-accent3-strongest color-accent3-strongest',
    name: 'text-accent3-strongest',
    featureReferenced: 'text-accent3-strongest'
  },
  'Piggybank Strong': {
    background: 'bg-[#581226]',
    testimonial: 'text-accent5-base color-accent5-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Piggybank Subtle': {
    background: 'bg-[#FFE8F1]',
    testimonial: 'text-accent5-strongest color-accent5-strongest',
    name: 'text-accent5-strongest',
    featureReferenced: 'text-accent5-strongest'
  },
  'Tangerine Strong': {
    background: 'bg-accent4-strongest',
    testimonial: 'text-accent4-base color-accent4-base',
    name: 'text-[#FFF]',
    featureReferenced: 'text-[#FFF]'
  },
  'Tangerine Subtle': {
    background: 'bg-[#FFF1DD]',
    testimonial: 'text-accent4-strongest color-accent4-strongest',
    name: 'text-accent4-strongest',
    featureReferenced: 'text-accent4-strongest'
  }
};

interface TestimonialDescriptionsProps {
  testimonialDescriptions: IBuildingBlockTestimonialDescription[];
  fillCardWithImage?: boolean;
  cardTheme?: string;
}

export function TestimonialDescriptions({
  cardTheme,
  testimonialDescriptions
}: TestimonialDescriptionsProps) {
  const richTextRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement | null>(null);
  const [anyOverflowing, setAnyOverflowing] = useState<boolean>(false);

  useEffect(() => {
    const checkHeights = () => {
      const newOverflowing = richTextRefs.current.map((ref) => {
        if (!ref) return false;
        const isMobile = window.innerWidth < 720;
        const maxHeight = isMobile ? 160 : 290;
        return ref.offsetHeight > maxHeight;
      });
      setAnyOverflowing(newOverflowing.some(Boolean));
    };

    checkHeights();
    window.addEventListener('resize', checkHeights);
    return () => window.removeEventListener('resize', checkHeights);
  }, [testimonialDescriptions]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const thumb = scrollbarThumbRef.current;
    if (!container || !thumb) return;

    const updateScrollProgress = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0) {
        thumb.style.left = '0%';
        thumb.style.width = '100%';
        return;
      }

      const scrollPercentage = (scrollLeft / maxScroll) * 100;
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbLeft = scrollPercentage * ((100 - thumbWidth) / 100);

      thumb.style.left = `${thumbLeft}%`;
      thumb.style.width = `${thumbWidth}%`;
    };

    updateScrollProgress();
    container.addEventListener('scroll', updateScrollProgress, {
      passive: true
    });
    window.addEventListener('resize', updateScrollProgress);

    return () => {
      container.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('resize', updateScrollProgress);
    };
  }, [testimonialDescriptions]);

  return (
    <div className="relative w-full overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto overflow-y-hidden scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="mb-11 flex w-max gap-4 pr-8 tablet:gap-6 tablet:pr-12">
          {(testimonialDescriptions || []).map(
            ({ sys, fields }: any, idx: number) => {
              const RICH_TEXT_OVERRIDE: RenderNode = {
                [BLOCKS.PARAGRAPH]: (_node, children) => (
                  <p
                    ref={(el) => (richTextRefs.current[idx] = el)}
                    className={cx(
                      anyOverflowing
                        ? 'tablet:typography-display-5 typography-body-1'
                        : 'tablet:typography-display-4 typography-body-1',
                      'font-normal',
                      {
                        [SocialQuoteCardColorTheme[
                          (cardTheme ||
                            'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                        ]?.testimonial]: !!cardTheme
                      }
                    )}
                  >
                    {children}
                  </p>
                )
              };

              return (
                <div
                  key={sys.id}
                  className="flex aspect-[3/4] max-h-[290px] flex-shrink-0 flex-col items-center tablet:max-h-[450px]"
                >
                  {fields?.cardType === TestimonialCardType.FullWidthImage ? (
                    <div
                      data-testid="full-width-image-card"
                      className="relative z-0 aspect-[56/75] h-[320px] overflow-hidden rounded-2xl tablet:h-[450px]"
                    >
                      <Asset
                        asset={fields?.cardImage}
                        className="absolute inset-0 h-full w-full rounded-2xl object-cover"
                        loading="eager"
                      />
                    </div>
                  ) : (
                    <div
                      className={cx(
                        'z-0 flex h-full w-full flex-col justify-between gap-4 rounded-2xl p-4 tablet:gap-6 tablet:p-6',
                        {
                          [SocialQuoteCardColorTheme[
                            (cardTheme ||
                              'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                          ]?.background]: !!cardTheme,
                          'bg-warm-neutral-100': !cardTheme
                        }
                      )}
                    >
                      <div
                        className={cx('relative flex flex-shrink-0', {
                          'size-10 tablet:size-16':
                            fields?.cardType === TestimonialCardType.Avatar,
                          'h-10 w-[40px] items-center tablet:h-16':
                            fields?.cardType === TestimonialCardType.BentoIcon,
                          'h-10 w-[136px] tablet:h-16':
                            fields?.cardType === TestimonialCardType.Star
                        })}
                      >
                        {fields?.cardType === TestimonialCardType.Star && (
                          <StarRatingSVG
                            className={cx(
                              'absolute left-0 top-1/2 h-6 w-full -translate-y-1/2',
                              {
                                [SocialQuoteCardColorTheme[
                                  (cardTheme ||
                                    'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                                ]?.testimonial]: !!cardTheme
                              }
                            )}
                          />
                        )}
                        {fields?.cardType === TestimonialCardType.Avatar && (
                          <Asset
                            asset={fields?.cardImage}
                            className="absolute inset-0 h-full w-full rounded-full object-cover"
                            loading="eager"
                          />
                        )}
                        {fields?.cardType === TestimonialCardType.BentoIcon && (
                          <Icon
                            className={cx('rotate-180', {
                              [SocialQuoteCardColorTheme[
                                (cardTheme ||
                                  'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                              ]?.testimonial]: !!cardTheme
                            })}
                            fileName={
                              fields?.iconCardTypeIcon?.fields?.iconType
                            }
                            size={'xxl'}
                            aria-label={
                              fields?.iconCardTypeIcon?.fields?.description ||
                              'Bento Icon'
                            }
                            alt={
                              fields?.iconCardTypeIcon?.fields?.description ||
                              'Bento Icon'
                            }
                          />
                        )}
                      </div>
                      <div className="flex flex-grow flex-col justify-between">
                        <div
                          className={classNames(
                            'flex flex-grow flex-col items-start text-left',
                            {
                              'max-h-[160px]': anyOverflowing,
                              'tablet:max-h-[290px]': true
                            }
                          )}
                        >
                          <div
                            className={classNames({
                              'line-clamp-6': anyOverflowing
                            })}
                          >
                            <RichText
                              data={fields?.testimonial}
                              overrideOptions={RICH_TEXT_OVERRIDE}
                            />
                          </div>
                        </div>
                        <div className="gap-0.5">
                          <div
                            className={cx(
                              'truncate text-left',
                              anyOverflowing
                                ? 'typography-title-5'
                                : 'typography-title-4',
                              {
                                [SocialQuoteCardColorTheme[
                                  (cardTheme ||
                                    'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                                ]?.name]: !!cardTheme
                              }
                            )}
                            title={fields?.name}
                          >
                            {fields?.name}
                          </div>
                          <div
                            className={cx(
                              'truncate text-left font-[350]',
                              anyOverflowing
                                ? 'typography-title-5'
                                : 'typography-title-4',
                              {
                                [SocialQuoteCardColorTheme[
                                  (cardTheme ||
                                    'Brand Strong') as keyof typeof SocialQuoteCardColorTheme
                                ]?.featureReferenced]: !!cardTheme
                              }
                            )}
                            title={fields?.featureReferenced}
                          >
                            {fields?.featureReferenced}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>
      <div className="relative h-1 w-full rounded-full bg-[#A5F3E1]">
        <div
          ref={scrollbarThumbRef}
          className="absolute h-full rounded-full bg-[#00D9B4]"
          style={{
            left: '0%',
            width: '100%'
          }}
        />
      </div>
    </div>
  );
}
